import ISupplierModel from "@models/ISupplierModel";
import IUserUI from "@models/IUserUI";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

export interface IShipmentsFilterLookups {
    vendors: (text: string) => Promise<IFilterSelectOption[]>;
    resolveVendor?: (guid: string) => Promise<IFilterSelectOption | undefined>;
}

export interface IShipmentsFilterContext {
    supplierModel: ISupplierModel;
    userModel: IUserUI;
    lookups: IShipmentsFilterLookups;
    /** Translate a message id (used for static option labels) */
    formatMessage: (id: string) => string;
}

/** Baseline Shipments filters: what "All Shipments", Reset and Clear all return to (legacy default `?hide_signed=true`). */
export const SHIPMENTS_DEFAULT_FILTERS: FilterValues = {
    hide_signed: true,
};

/**
 * Compile the Shipments filter config. Field keys ARE the wire names (apiKey) of the
 * `getShipmentGroups` queryString: vendor, order, po_no, packing_list, shipment_type, has_shipments, hide_signed.
 * Visibility gates mirror the legacy ShipmentFilterFn.
 */
export function buildShipmentsFilterSections(ctx: IShipmentsFilterContext): IFilterSection[] {
    const { supplierModel, userModel, formatMessage } = ctx;
    const isSupplierUser = !!userModel.isSupplierUser;

    const vendorLabel =
        supplierModel.vendor_name_override && supplierModel.vendor_name_override !== "null" ? supplierModel.vendor_name_override : "vendor";

    const typeOptions: IFilterSelectOption[] = [
        { label: formatMessage("all"), value: "" },
        { label: formatMessage("shipment"), value: "shipment" },
    ];

    if (supplierModel.has_local_pickup) {
        typeOptions.push({ label: formatMessage("local_pickup"), value: "local_pickup" });
    }

    if (supplierModel.has_local_delivery) {
        typeOptions.push({ label: formatMessage("local_delivery"), value: "local_delivery" });
    }

    if (supplierModel.has_dropshipping) {
        typeOptions.push({ label: formatMessage("dropshipment"), value: "dropshipment" });
    }

    const main: IFilterField[] = [];

    if (isSupplierUser) {
        main.push({
            key: "vendor",
            label: vendorLabel,
            type: "typeahead",
            apiKey: "vendor",
            lookup: ctx.lookups.vendors,
            resolveOption: ctx.lookups.resolveVendor,
        });
    }

    main.push({ key: "order", label: "order", type: "text", apiKey: "order" });
    main.push({ key: "po_no", label: "po_no", type: "text", apiKey: "po_no" });

    if (supplierModel.has_pick_list_management) {
        main.push({ key: "packing_list", label: "packing_list", type: "text", apiKey: "packing_list" });
    }

    main.push({ key: "shipment_type", label: "type", type: "select", apiKey: "shipment_type", options: typeOptions });
    main.push({ key: "has_shipments", label: "has_shipments", type: "toggle", apiKey: "has_shipments" });

    if (supplierModel.has_local_delivery || supplierModel.has_local_pickup) {
        main.push({ key: "hide_signed", label: "hide_signed", type: "toggle", apiKey: "hide_signed" });
    }

    return [{ id: "main", title: "filters", filters: main }];
}
